"use client";

import { ReactNode, useEffect, useRef, useState } from "react";
import { motion, useAnimationFrame } from "framer-motion";

interface MarqueeCarouselProps {
    children: ReactNode[];
    speed?: number; // pixels per second
    pauseOnHover?: boolean;
    direction?: "left" | "right";
    className?: string;
}

export function MarqueeCarousel({
    children,
    speed = 50,
    pauseOnHover = true,
    direction = "left",
    className = "",
}: MarqueeCarouselProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollerRef = useRef<HTMLDivElement>(null);
    const [isPaused, setIsPaused] = useState(false);
    const [scrollPosition, setScrollPosition] = useState(0);

    // Duplicate children for seamless loop
    const duplicatedChildren = [...children, ...children];

    useAnimationFrame((time, delta) => {
        if (isPaused || !scrollerRef.current) return;

        const pixelsPerFrame = (speed * delta) / 1000;
        const movement = direction === "left" ? pixelsPerFrame : -pixelsPerFrame;

        setScrollPosition((prev) => {
            const newPosition = prev + movement;
            const halfWidth = scrollerRef.current!.scrollWidth / 2;

            // Reset position when we've scrolled through the first set of items
            if (direction === "left" && newPosition >= halfWidth) {
                return newPosition - halfWidth;
            }
            if (direction === "right" && newPosition <= -halfWidth) {
                return newPosition + halfWidth;
            }

            return newPosition;
        });
    });

    return (
        <div
            ref={containerRef}
            className={`overflow-hidden ${className}`}
            onMouseEnter={() => pauseOnHover && setIsPaused(true)}
            onMouseLeave={() => pauseOnHover && setIsPaused(false)}
        >
            <motion.div
                ref={scrollerRef}
                className="flex gap-6"
                style={{
                    x: -scrollPosition,
                }}
            >
                {duplicatedChildren.map((child, index) => (
                    <div key={index} className="flex-shrink-0">
                        {child}
                    </div>
                ))}
            </motion.div>
        </div>
    );
}
